import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth-context';
import { useCart } from '@/lib/cart-context';
import { useWishlist } from '@/lib/wishlist-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, ShoppingCart, User, Heart, Package, LogOut, Settings, LayoutDashboard, Store, Loader2, X, Sparkles } from 'lucide-react';
import { LanguageSelector } from '@/components/LanguageSelector';
import { CurrencySelector } from '@/components/CurrencySelector';
import { products, categories } from '@/lib/api';
import { buildPathForLocale } from '@/lib/intl-seo';
import { isExactHomepagePath, requestHomepageRefresh } from '@/lib/homePageVisibility';
import { cn } from '@/lib/utils';
import { SearchSuggestionsDropdown } from '@/components/layout/SearchSuggestionsDropdown';

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { totalItems } = useCart();
  const { count: wishlistCount } = useWishlist();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const lc = (i18n.language || 'en').substring(0, 2);
  const navPath = (...segs) => buildPathForLocale(lc, segs);
  
  // Autocomplete state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const [allCategories, setAllCategories] = useState([]);
  useEffect(() => {
    let cancelled = false;
    categories
      .list()
      .then((res) => {
        if (!cancelled) {
          const data = res.data;
          setAllCategories(Array.isArray(data) ? data : []);
        }
      })
      .catch(() => {
        if (!cancelled) setAllCategories([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const matchedCategories = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 1 || !allCategories.length) return [];
    return allCategories
      .filter((c) => {
        const name = (c.name || '').toLowerCase();
        const slug = (c.slug || '').toLowerCase();
        return name.includes(q) || slug.includes(q);
      })
      .slice(0, 8);
  }, [searchQuery, allCategories]);

  const normSlugFold = useCallback((s) => {
    return String(s || '')
      .toLowerCase()
      .normalize('NFKD')
      .replace(/\p{M}/gu, '')
      .replace(/[^a-z0-9]+/gi, '');
  }, []);

  /** Navbar slug'ları API kategorisiyle doğrula (tam eşleşme önce). */
  const resolveCategorySlug = useCallback(
    (candidates) => {
      const cats = Array.isArray(allCategories) ? allCategories : [];
      const list = Array.isArray(candidates) ? candidates : [candidates];
      for (const want of list) {
        const w = String(want || '').trim();
        if (!w) continue;
        const exact = cats.find((c) => (c.slug || '') === w);
        if (exact?.slug) return exact.slug;
      }
      for (const want of list) {
        const f = normSlugFold(want);
        if (f.length < 2) continue;
        const m = cats.find((c) => normSlugFold(c.slug) === f);
        if (m?.slug) return m.slug;
      }
      const first = list.find(Boolean);
      return first ? String(first) : '';
    },
    [allCategories, normSlugFold]
  );

  /**
   * “Moda / Giyim” menüsü: genel giyim (kadın‑giyim / erkek‑giyim yoksa tercih et).
   * DB’de üst kategori “Giyim” vb. varsa ona gider.
   */
  const resolveFashionNavSlug = useCallback(() => {
    const cats = Array.isArray(allCategories) ? allCategories : [];
    const blockedSlug = (slug) =>
      /kadin|erkek|women|mens|kids|cocuk|bebek|man\b|boy\b|girl/i.test(String(slug || ''));
    const prefer = [
      'giyim',
      'moda-giyim',
      'moda',
      'kislik-giyim',
      'kiyafet',
      'kıyafet',
      'giyim-aksesuar',
      'giyim-ve-aksesuar',
    ];
    for (const cand of prefer) {
      const hit = cats.find(
        (c) => c.slug && !blockedSlug(c.slug) && normSlugFold(c.slug) === normSlugFold(cand)
      );
      if (hit?.slug) return hit.slug;
    }
    const byName = cats.find((c) => {
      const n = (c.name || '').toLowerCase();
      const sl = String(c.slug || '');
      if (!sl || blockedSlug(sl)) return false;
      if (/\b(kadın|erkek|bebek|çocuk|cocuk|women|mens)\b/i.test(n)) return false;
      if (/\bgenel giyim\b|\btum giyim\b/i.test(n)) return true;
      if (/^moda\s*&\s*(giyim|moda)$/i.test(n.trim())) return true;
      if (/\bgiyim\b/.test(n) && !/\bkadın.*giyim|\berkek.*giyim/i.test(n)) return true;
      return /^moda$/i.test((c.name || '').trim());
    });
    if (byName?.slug) return byName.slug;
    const fb = resolveCategorySlug(prefer);
    if (fb && !blockedSlug(fb)) return fb;
    const loose = cats.find((c) => {
      const sl = String(c.slug || '');
      if (!sl || blockedSlug(sl)) return false;
      return /\bgiyim\b|\bkiyafet\b/i.test(String(c.name || ''));
    });
    return loose?.slug || fb || '';
  }, [allCategories, normSlugFold, resolveCategorySlug]);

  /** Header «Kitaplar»: önce gerçek kitap slug’ları, en sonda kırtasiye yedek (aynı isimle iki menü karışmasın). */
  const resolveBooksNavSlug = useCallback(() => {
    const candidates = [
      'kitaplar',
      'kitap',
      'kitaplar-ve-dergiler',
      'edebiyat',
      'cocuk-kitaplari',
      'kirtasiye',
    ];
    return resolveCategorySlug(candidates);
  }, [resolveCategorySlug]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search with debounce (panel ile aynı API: GET /products + dil + relevance sıralaması)
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    const trimmed = searchQuery.trim();

    if (trimmed.length < 1) {
      setSearchResults([]);
      setShowResults(false);
      setIsSearching(false);
      return () => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      };
    }

    // Kısa debounce + hafif /products?autocomplete (backend: count yok, ince alan, satıcı join yok)
    const debounceMs = trimmed.length === 1 ? 200 : 140;
    const limit = trimmed.length === 1 ? 6 : 10;
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await products.list({
          search: trimmed,
          limit,
          page: 1,
          lang: lc,
          sort: 'created_at',
          order: 'desc',
          autocomplete: true,
        });
        setSearchResults(response.data.products || []);
        setShowResults(true);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, debounceMs);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery, lc]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`${navPath('products')}?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowResults(false);
      setSearchQuery('');
    }
  };

  const handleAiSearch = () => {
    const q = searchQuery.trim();
    const base = navPath('ai');
    navigate(q.length >= 2 ? `${base}?q=${encodeURIComponent(q)}` : base);
    setShowResults(false);
    setSearchQuery('');
  };

  const handleResultClick = (productId) => {
    navigate(navPath('product', String(productId)));
    setSearchQuery('');
    setShowResults(false);
  };

  const handleCategoryClick = (slug) => {
    if (!slug) return;
    navigate(navPath('category', encodeURIComponent(String(slug))));
    setSearchQuery('');
    setShowResults(false);
  };

  const handleLogout = () => {
    logout();
    navigate(navPath());
  };

  const searchPanelOpen = showResults && searchQuery.trim().length >= 1;
  const qTrim = searchQuery.trim();

  // Desktop category menu items — slug'lar yüklenen kategori listesiyle doğrulanır
  // Sıra: Aktar → Uçak → Araç → Otel → diğerleri (Taxi Limuzin yok)
  const categoryMenuItems = useMemo(
    () => [
      { key: 'transfer', label: t('headerNav.transfer'), href: navPath('category', 'aktar') },
      { key: 'flights', label: t('headerNav.flights'), href: navPath('flights') },
      { key: 'car-rental', label: t('headerNav.carRental'), href: navPath('car-rentals') },
      { key: 'hotels', label: t('headerNav.hotels'), href: navPath('hotels') },
      { key: 'supermarket', label: t('headerNav.supermarkets'), href: navPath('supermarkets') },
      {
        key: 'electronics',
        label: t('headerNav.electronics'),
        href: navPath('category', encodeURIComponent(resolveCategorySlug(['elektronik']))),
      },
      {
        key: 'fashion',
        label: t('headerNav.fashion'),
        href: navPath('category', encodeURIComponent(resolveFashionNavSlug())),
      },
      {
        key: 'home-garden',
        label: t('headerNav.homeGarden'),
        href: navPath(
          'category',
          encodeURIComponent(resolveCategorySlug(['ev-&-yasam', 'ev-yasam', 'ev-yasam-1'])),
        ),
      },
      {
        key: 'sports',
        label: t('headerNav.sports'),
        href: navPath('category', encodeURIComponent(resolveCategorySlug(['spor']))),
      },
      {
        key: 'books',
        label: t('headerNav.books'),
        href: navPath('category', encodeURIComponent(resolveBooksNavSlug())),
      },
      { key: 'restaurants', label: t('headerNav.restaurants'), href: navPath('restaurants') },
      { key: 'membership', label: t('headerNav.membership'), href: navPath('membership') },
      { key: 'deals', label: t('headerNav.deals'), href: `${navPath('products')}?featured=true`, highlight: true },
    ],
    [t, navPath, resolveCategorySlug, resolveFashionNavSlug, resolveBooksNavSlug],
  );

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
      {/* Top Promo Bar */}
      <div className="bg-primary text-primary-foreground py-1.5 text-center text-xs font-medium tracking-wide">
        {t('promo.welcomeBanner', "Dünyanın ilk ve tek Türk E-Ticaret Platformuna Hoşgeldiniz!")}
      </div>

      {/* Main Header */}
      <div className="container mx-auto px-1.5 sm:px-4">
        <div className="flex items-center h-12 sm:h-14 md:h-16 gap-1 sm:gap-2 md:gap-3">
          {/* Logo */}
          <Link
            to={navPath()}
            className="flex-shrink-0"
            data-testid="logo"
            onClick={(event) => {
              if (!isExactHomepagePath(location.pathname, lc)) return;
              event.preventDefault();
              requestHomepageRefresh();
            }}
          >
            <img src="/logo.png" alt="Turkish E-Market" className="h-9 sm:h-10 md:h-11 w-auto object-contain" />
          </Link>

          {/* Search Bar - Desktop/Tablet only */}
          <form 
            onSubmit={handleSearch} 
            className="hidden sm:flex flex-1 min-w-0 relative mx-2 md:mx-3" 
            ref={searchRef}
          >
            <div className="relative w-full">
              <div
                className={cn(
                  'relative flex w-full items-center h-10 pl-3 pr-2 gap-1 bg-background transition-shadow',
                  searchPanelOpen
                    ? 'border-2 border-orange-500 border-b-0 rounded-t-2xl shadow-sm z-[101]'
                    : 'border-2 border-primary/20 rounded-full focus-within:border-primary/40'
                )}
              >
                <Input
                  type="text"
                  inputMode="search"
                  enterKeyHint="search"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  placeholder={t('search.placeholder', 'Ürün, araç veya otel ara...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 flex-1 min-w-0 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 pl-1 pr-1 text-sm [&::-webkit-search-cancel-button]:hidden"
                  data-testid="search-input"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchResults([]);
                      setShowResults(false);
                    }}
                    className="p-1 rounded-full text-muted-foreground hover:bg-muted"
                    aria-label={t('common.clear', 'Temizle')}
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                {isSearching && (
                  <Loader2 className="h-4 w-4 shrink-0 animate-spin text-orange-500" />
                )}
                <button
                  type="submit"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-neutral-700 hover:bg-neutral-100"
                  aria-label={t('common.search', 'Ara')}
                  data-testid="search-submit"
                >
                  <Search className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={handleAiSearch}
                  data-testid="ai-search-btn"
                  className="inline-flex h-7 shrink-0 items-center gap-1 rounded-full border border-neutral-300 bg-white pl-2 pr-2.5 text-xs font-medium text-neutral-800 hover:bg-neutral-50"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {t('search.aiButton', 'AI Modu')}
                </button>
              </div>
              {searchPanelOpen && (
                <SearchSuggestionsDropdown
                  panelOpen={searchPanelOpen}
                  queryTrimmed={qTrim}
                  isSearching={isSearching}
                  matchedCategories={matchedCategories}
                  searchResults={searchResults}
                  onProductPick={handleResultClick}
                  onCategoryPick={handleCategoryClick}
                />
              )}
            </div>
          </form>

          {/* Right Side Actions - Push to right */}
          <div className="flex items-center gap-0 sm:gap-1 md:gap-2 flex-shrink-0 ml-auto">
            
            {/* Language & Currency - Desktop Only (full version) */}
            <div className="hidden lg:flex items-center gap-1">
              <LanguageSelector />
              <CurrencySelector />
            </div>
            
            {/* Language & Currency - Mobile/Tablet (compact icons) */}
            <div className="flex lg:hidden items-center gap-0.5">
              <LanguageSelector compact={true} />
              <CurrencySelector compact={true} />
            </div>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative h-8 w-8 hidden sm:flex" data-testid="user-menu-btn">
                    <User className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm font-medium border-b mb-1">
                    {user.name || user.email}
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/orders')}>
                    <Package className="mr-2 h-4 w-4" />
                    {t('nav.orders', 'Siparişlerim')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    {t('nav.settings', 'Ayarlar')}
                  </DropdownMenuItem>
                  {(user.role === 'admin' || user.admin_role) && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')}>
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        {t('nav.adminPanel', 'Admin Paneli')}
                      </DropdownMenuItem>
                    </>
                  )}
                  {user.role === 'seller' && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/seller')}>
                        <Store className="mr-2 h-4 w-4" />
                        {t('nav.sellerPanel', 'Satıcı Paneli')}
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logout', 'Çıkış Yap')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/login')}
                className="hidden sm:flex items-center justify-center h-8 w-8"
                data-testid="login-btn"
              >
                <User className="h-4 w-4" />
              </Button>
            )}

            {/* Favorites - Hidden on mobile (in bottom nav) */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(user ? '/account/wishlist' : '/login')}
              className="relative hidden sm:flex h-8 w-8"
              data-testid="favorites-btn"
            >
              <Heart className={`h-4 w-4 ${wishlistCount > 0 ? 'text-red-500 fill-red-500' : ''}`} />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center">
                  {wishlistCount > 99 ? '99+' : wishlistCount}
                </span>
              )}
            </Button>

            {/* Cart - Hidden on mobile (in bottom nav) */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/cart')}
              className="relative hidden sm:flex h-8 w-8"
              data-testid="cart-btn"
            >
              <ShoppingCart className="h-4 w-4" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                  {totalItems > 99 ? '99+' : totalItems}
                </span>
              )}
            </Button>

            {/* Become Seller - All screens */}
            {!user?.role?.includes('seller') && (
              <Button 
                variant="outline"
                size="sm"
                onClick={() => navigate(navPath('become-seller'))}
                className="flex items-center gap-0.5 text-[10px] sm:text-xs border-primary text-primary hover:bg-primary hover:text-white px-1.5 sm:px-2 h-7 sm:h-8"
                data-testid="become-seller-btn"
              >
                <Store className="h-4 w-4" />
                <span>{t('nav.becomeSeller', 'Satıcı Ol')}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop Category Navigation Bar */}
      <div className="hidden lg:block bg-white border-t border-gray-100">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-start gap-1 overflow-x-auto py-2">
            {categoryMenuItems.map((item, index) => (
              <Link
                key={item.key}
                to={item.href}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-all rounded hover:bg-gray-100 ${
                  index === 0 
                    ? 'text-teal-600 hover:text-teal-700' 
                    : item.highlight 
                      ? 'text-orange-600 hover:text-orange-700' 
                      : 'text-gray-700 hover:text-gray-900'
                }`}
                data-testid={`nav-category-${item.key}`}
              >
                <span>{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
